import { useRef, useState } from 'react';
import { useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { AdminDeviceMqtt, SensorMapping } from '../../../domain/Admin';

// Los spots publican cada pocos segundos: se refresca para ver los valores
// en vivo mientras se arma el mapeo.
const REFRESH_MS = 5000;

export function useAdminDeviceMqtt(deviceId: string) {
  const { admin } = useRepositories();
  const [data, setData] = useState<AdminDeviceMqtt | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Topic en el Mosquitto propio, editable.
  const [topic, setTopic] = useState('');
  const [linking, setLinking] = useState(false);

  // Mapeos editados y todavía sin guardar, por sensor: el refresco no los pisa.
  const [drafts, setDrafts] = useState<Record<string, SensorMapping>>({});
  const [savingSensor, setSavingSensor] = useState<string | null>(null);

  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = async () => {
    try {
      const res = await admin.fetchDeviceMqtt(deviceId);
      setData(res);
      return res;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar la estación.');
      return null;
    }
  };

  // Todo lo de la pantalla se setea junto, recién cuando llegó: si no, se
  // dibuja primero con el topic vacío y un instante después
  // salta a los valores reales. El spinner es solo si todavía no hay datos de
  // esta estación (al volver a la pantalla se actualiza sin moverla).
  useIonViewWillEnter(() => {
    const firstLoad = data?.device.id !== deviceId;
    if (firstLoad) {
      setData(null);
      setLoading(true);
    }
    setError(null);
    admin
      .fetchDeviceMqtt(deviceId)
      .then((res) => {
        setData(res);
        setTopic(res.device.mqttTopic);
        setDrafts({});
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'No se pudo cargar la estación.'))
      .finally(() => setLoading(false));
    timer.current = setInterval(load, REFRESH_MS);
  });

  useIonViewWillLeave(() => {
    if (timer.current) clearInterval(timer.current);
  });

  const handleLink = async () => {
    setLinking(true);
    setError(null);
    try {
      await admin.setDeviceTopic(deviceId, topic);
      await load();
      setToast('Topic guardado.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar el topic.');
    } finally {
      setLinking(false);
    }
  };

  const mappingFor = (sensorId: string): SensorMapping =>
    drafts[sensorId] ?? data?.sensors.find((s) => s.id === sensorId)?.mapping ?? {};

  const setFieldTarget = (sensorId: string, field: string, target: SensorMapping[string] | null) => {
    const next = { ...mappingFor(sensorId) };
    if (target) next[field] = target;
    else delete next[field];
    setDrafts((prev) => ({ ...prev, [sensorId]: next }));
  };

  const saveMapping = async (sensorId: string) => {
    setSavingSensor(sensorId);
    setError(null);
    try {
      await admin.updateSensorMapping(deviceId, sensorId, mappingFor(sensorId));
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[sensorId];
        return next;
      });
      await load();
      setToast('Mapeo guardado.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar el mapeo.');
    } finally {
      setSavingSensor(null);
    }
  };

  return {
    data,
    loading,
    error,
    toast,
    setToast,
    topic,
    setTopic,
    linking,
    handleLink,
    drafts,
    mappingFor,
    setFieldTarget,
    saveMapping,
    savingSensor,
  };
}
